import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { SITE_ID } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';

interface QAItem {
  id: string;
  soran_adi: string;
  soru_metni: string;
  cevap_metni: string | null;
  cevaplandi: boolean;
  olusturma_tarihi: string;
  cevap_tarihi: string | null;
}

interface ProductQAProps {
  productId: string;
  anaUrunId?: string;
  onCountChange?: (count: number) => void;
}

export default function ProductQA({ productId, anaUrunId, onCountChange }: ProductQAProps) {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<QAItem[]>([]);
  const [loadingQ, setLoadingQ] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({ soran_adi: '', soran_email: '', soru_metni: '' });

  useEffect(() => {
    let cancelled = false;
    async function fetchQuestions() {
      setLoadingQ(true);

      // Varyasyon ise ana ürünün sorularını da dahil et
      const targetIds = [productId];
      if (anaUrunId) targetIds.push(anaUrunId);

      const { data } = await supabase
        .from('urun_sorulari')
        .select('id, soran_adi, soru_metni, cevap_metni, cevaplandi, olusturma_tarihi, cevap_tarihi')
        .eq('site_id', SITE_ID)
        .in('urun_id', targetIds)
        .eq('cevaplandi', true)
        .order('olusturma_tarihi', { ascending: false });

      if (!cancelled) {
        const fetched = data || [];
        setQuestions(fetched);
        setLoadingQ(false);
        onCountChange?.(fetched.length);
      }
    }
    if (productId) fetchQuestions();
    return () => { cancelled = true; };
  }, [productId, anaUrunId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const soranAdi = user ? (user.user_metadata?.full_name || user.email || '') : form.soran_adi.trim();
    const soranEmail = user ? (user.email || '') : form.soran_email.trim();

    if (!soranAdi || !form.soru_metni.trim()) {
      setError('Lütfen sorunuzu girin.');
      return;
    }
    if (form.soru_metni.length > 500) {
      setError('Soru en fazla 500 karakter olabilir.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error: insertErr } = await supabase.from('urun_sorulari').insert({
      site_id: SITE_ID,
      urun_id: productId,
      soran_adi: soranAdi,
      soran_email: soranEmail || null,
      soru_metni: form.soru_metni.trim(),
      cevaplandi: false,
    });
    setSubmitting(false);
    if (insertErr) {
      setError('Soru gönderilemedi, lütfen tekrar deneyin.');
    } else {
      setSubmitted(true);
      setForm({ soran_adi: '', soran_email: '', soru_metni: '' });
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-10">
      {/* Cevaplanmış Sorular */}
      <div>
        <h3 className="font-serif text-base font-semibold text-coffee mb-5">
          Cevaplanmış Sorular
          {questions.length > 0 && (
            <span className="ml-2 text-xs font-normal text-coffee-light">({questions.length} soru)</span>
          )}
        </h3>

        {loadingQ ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="animate-pulse space-y-2 bg-white rounded-xl p-5 border border-cream-dark">
                <div className="h-3 bg-cream rounded w-1/4" />
                <div className="h-4 bg-cream rounded w-3/4" />
                <div className="h-3 bg-cream rounded w-1/2 mt-3" />
              </div>
            ))}
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-xl border border-cream-dark">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-cream rounded-full">
              <i className="ri-question-line text-2xl text-coffee-light" />
            </div>
            <p className="text-sm text-coffee-light">Henüz cevaplanmış soru yok.</p>
            <p className="text-xs text-coffee-light/70 mt-1">İlk soruyu siz sorun!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map(q => (
              <div key={q.id} className="bg-white rounded-xl border border-cream-dark overflow-hidden">
                {/* Soru */}
                <div className="flex items-start gap-3 px-5 py-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-coffee/10 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-question-line text-coffee text-sm" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-coffee">{q.soran_adi}</span>
                      <span className="text-xs text-coffee-light">{formatDate(q.olusturma_tarihi)}</span>
                    </div>
                    <p className="text-sm text-coffee-medium leading-relaxed">{q.soru_metni}</p>
                  </div>
                </div>
                {/* Cevap */}
                {q.cevap_metni && (
                  <div className="flex items-start gap-3 px-5 py-4 bg-gold/5 border-t border-cream-dark">
                    <div className="w-7 h-7 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-customer-service-2-line text-gold text-sm" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-gold">Satıcı Cevabı</span>
                        {q.cevap_tarihi && (
                          <span className="text-xs text-coffee-light">{formatDate(q.cevap_tarihi)}</span>
                        )}
                      </div>
                      <p className="text-sm text-coffee-medium leading-relaxed">{q.cevap_metni}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Soru Gönderme Formu */}
      <div className="bg-cream/50 rounded-2xl p-6 border border-cream-dark">
        <h3 className="font-serif text-base font-semibold text-coffee mb-1">Ürün Hakkında Soru Sor</h3>
        <p className="text-xs text-coffee-light mb-5">Sorunuz incelendikten sonra bu sayfada yayınlanacaktır.</p>

        {submitted ? (
          <div className="flex items-center gap-3 py-4 px-5 bg-green-50 border border-green-200 rounded-xl">
            <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
              <i className="ri-check-line text-green-600 text-base" />
            </div>
            <div>
              <p className="text-sm font-semibold text-green-700">Sorunuz alındı!</p>
              <p className="text-xs text-green-600 mt-0.5">En kısa sürede cevaplanacak ve burada yayınlanacak.</p>
            </div>
            <button
              onClick={() => setSubmitted(false)}
              className="ml-auto text-xs text-green-600 underline cursor-pointer whitespace-nowrap"
            >
              Yeni soru sor
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Giriş yapmış kullanıcıya bilgi satırı */}
            {user && (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-cream-dark rounded-xl">
                <div className="w-6 h-6 flex items-center justify-center bg-coffee/10 rounded-full flex-shrink-0">
                  <i className="ri-user-line text-coffee text-xs" />
                </div>
                <span className="text-xs text-coffee-medium">
                  <span className="font-semibold text-coffee">{user.user_metadata?.full_name || user.email}</span> olarak soru soruyorsunuz
                </span>
              </div>
            )}

            {/* Giriş yapmamış kullanıcıdan ad/e-posta al */}
            {!user && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-coffee mb-1.5">
                    Adınız <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="soran_adi"
                    value={form.soran_adi}
                    onChange={handleChange}
                    placeholder="Adınız Soyadınız"
                    maxLength={100}
                    className="w-full px-4 py-2.5 text-sm bg-white border border-cream-dark rounded-xl focus:outline-none focus:border-gold text-coffee placeholder:text-coffee-light/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-coffee mb-1.5">
                    E-posta <span className="text-coffee-light font-normal">(isteğe bağlı)</span>
                  </label>
                  <input
                    type="email"
                    name="soran_email"
                    value={form.soran_email}
                    onChange={handleChange}
                    placeholder="ornek@email.com"
                    className="w-full px-4 py-2.5 text-sm bg-white border border-cream-dark rounded-xl focus:outline-none focus:border-gold text-coffee placeholder:text-coffee-light/50 transition-colors"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-coffee mb-1.5">
                Sorunuz <span className="text-red-400">*</span>
              </label>
              <textarea
                name="soru_metni"
                value={form.soru_metni}
                onChange={handleChange}
                placeholder="Ürün hakkında merak ettiğiniz her şeyi sorabilirsiniz..."
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 text-sm bg-white border border-cream-dark rounded-xl focus:outline-none focus:border-gold text-coffee placeholder:text-coffee-light/50 transition-colors resize-none"
              />
              <p className="text-xs text-coffee-light mt-1 text-right">{form.soru_metni.length}/500</p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 px-4 py-2.5 rounded-xl border border-red-100">
                <i className="ri-error-warning-line text-base" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2.5 bg-coffee text-white text-sm font-semibold rounded-xl hover:bg-coffee-medium transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line text-base" />
                  Soruyu Gönder
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
